import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createMigrate,
  persistStore,
  persistReducer,
  PersistConfig,
  type MigrationManifest,
  type PersistedState,
} from 'redux-persist';

import storage from 'redux-persist/lib/storage';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';

import { productsApi } from './products/productsApi';
import { productsReducer } from './products/slice';
import { workflowsReducer } from './workflows/slice';
import { identityReducer } from './identity/slice';
import { identityApi } from './identity/identityApi';
import { workflowApi } from './workflows/workflowApi';
import {
  stylistLooksApi,
  stylistPromptTemplatesApi,
  stylistWorkflowsApi,
} from './stylist/stylistApi';
import { stylesSearchApi } from './stylesSearch/stylesSearchApi';
import { categoryApi } from './category/categoryApi';
import stylistReducer from './stylist/slice';

const middleware = [
  productsApi.middleware,
  identityApi.middleware,
  workflowApi.middleware,
  stylistPromptTemplatesApi.middleware,
  stylistWorkflowsApi.middleware,
  stylistLooksApi.middleware,
  stylesSearchApi.middleware,
  categoryApi.middleware,
];

const migrations: MigrationManifest = {
  1: (state: PersistedState) => {
    if (!state || typeof state !== 'object' || !('identity' in state)) {
      return state;
    }

    const stateRecord = state as PersistedState & Record<string, unknown>;
    const identityState =
      typeof stateRecord.identity === 'string'
        ? JSON.parse(stateRecord.identity)
        : stateRecord.identity;

    const migratedIdentity = {
      token: (identityState as { token?: string | null })?.token ?? null,
      refreshToken: (identityState as { refreshToken?: string | null })?.refreshToken ?? null,
      erpUser:
        (identityState as { erpUser?: unknown; tagLabUser?: unknown })?.erpUser ??
        (identityState as { erpUser?: unknown; tagLabUser?: unknown })?.tagLabUser ??
        null,
    };

    return {
      ...stateRecord,
      identity:
        typeof stateRecord.identity === 'string'
          ? JSON.stringify(migratedIdentity)
          : migratedIdentity,
    } as PersistedState;
  },
};

const rootReducer = combineReducers({
  identity: identityReducer,
  products: productsReducer,
  stylist: stylistReducer,
  [identityApi.reducerPath]: identityApi.reducer,
  [productsApi.reducerPath]: productsApi.reducer,
  [workflowApi.reducerPath]: workflowApi.reducer,
  [stylistPromptTemplatesApi.reducerPath]: stylistPromptTemplatesApi.reducer,
  [stylistWorkflowsApi.reducerPath]: stylistWorkflowsApi.reducer,
  [stylistLooksApi.reducerPath]: stylistLooksApi.reducer,
  [stylesSearchApi.reducerPath]: stylesSearchApi.reducer,
  [categoryApi.reducerPath]: categoryApi.reducer,
  workflows: workflowsReducer,
});

const persistConfig: PersistConfig<ReturnType<typeof rootReducer>> = {
  key: 'root',
  storage: storage,
  whitelist: ['identity'],
  version: 1,
  migrate: createMigrate(migrations, { debug: false }),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      thunk: true,
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(middleware),
  // devTools:
  //   ENV.VITE_ENVIRONMENT === 'local' || ENV.VITE_ENVIRONMENT === 'staging',
});

export type RootState = ReturnType<typeof rootReducer>;

export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const persistor = persistStore(store);
